import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, defer, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { ITeam, ITeamMember, TeamSortField, TeamSortMode } from '../../../models/settings.models';
import {
  AddTeam,
  AddTeamError,
  AddTeamSuccess,
  DeleteTeam,
  DeleteTeamSuccess,
  FetchAllTeams,
  SetTeamsLoader,
  UpdateTeam,
  UpdateTeamError,
  UpdateTeamSuccess,
} from '../../../store/settings.actions';
import { TeamDetailDialogComponent } from './team-detail-dialog/team-detail-dialog.component';
import { TeamFormDialogComponent, TeamFormPayload } from './team-form-dialog/team-form-dialog.component';

/** `"YYYY-MM-DD HH:mm:ss"` — the shape `ITeam.last_edited` actually arrives in. */
const LAST_EDITED_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/** How many member avatars fit in the cell before it collapses to the count alone. */
const VISIBLE_MEMBER_AVATARS = 4;

/** Shown in place of a value the API did not send. Same token as the users page. */
const EMPTY_STATE_VALUE = '--';

@Component({
  selector: 'app-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['./teams.component.scss'],
})
export class TeamsComponent implements OnInit, OnDestroy {
  // ── Form controls ─────────────────────────────────────────────────────────
  /**
   * Filtering happens in the browser. `GET /v1/teams` has no search parameter and
   * answers with every team in one response, so there is nothing to ask the API for
   * — unlike the users page, whose search is a server round-trip because its list
   * is paginated.
   */
  readonly searchControl = new FormControl('');

  // ── Store observables ─────────────────────────────────────────────────────
  readonly teams$: Observable<ITeam[]> = this.store.select((state) => state.settings.allTeams);
  // `loading` is one boolean shared by the whole settings feature (see 50-07). It is
  // read here only to keep the empty state from flashing before the first response.
  readonly loading$: Observable<boolean> = this.store.select((state) => state.settings.loading);
  /**
   * The rendered list. Row order is the API's — the sort is server-side — so this
   * only ever removes rows, it never reorders them.
   */
  readonly filteredTeams$: Observable<ITeam[]> = combineLatest([
    this.teams$,
    // `defer` so a subscription created after the user has typed still starts from
    // the term currently in the box; a bare `startWith` would freeze the seed at ''.
    defer(() => this.searchControl.valueChanges.pipe(startWith(this.searchControl.value))),
  ]).pipe(
    map(([teams, term]) => {
      const needle = (term ?? '').trim().toLowerCase();
      if (!needle) return teams ?? [];
      return (teams ?? []).filter((team) => (team.name ?? '').toLowerCase().includes(needle));
    }),
  );

  // ── Readonly refs ─────────────────────────────────────────────────────────
  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;
  readonly visibleMemberAvatars = VISIBLE_MEMBER_AVATARS;
  readonly emptyStateValue = EMPTY_STATE_VALUE;

  // ── Template state ────────────────────────────────────────────────────────
  sortField: TeamSortField | null = null;
  sortMode: TeamSortMode = 'asc';
  selectedTeam: ITeam | null = null;

  // ── Private state ─────────────────────────────────────────────────────────
  private readonly destroy$ = new Subject<void>();
  private teamFormDialogTeam: ITeam | null = null;
  private teamFormDialogRef: MatDialogRef<TeamFormDialogComponent> | null = null;
  private teamDetailDialogRef: MatDialogRef<TeamDetailDialogComponent> | null = null;

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private actions$: Actions,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.store.dispatch(SetTeamsLoader({ isLoading: true }));
    this.store.dispatch(FetchAllTeams({}));

    this.actions$
      .pipe(ofType(AddTeamSuccess, UpdateTeamSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeTeamFormDialog();
        this.refetchWithCurrentSort();
      });

    this.actions$
      .pipe(ofType(DeleteTeamSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.refetchWithCurrentSort());

    this.actions$
      .pipe(ofType(AddTeamError, UpdateTeamError), takeUntil(this.destroy$))
      .subscribe(() => this.teamFormDialogRef?.componentInstance.setSubmitting(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Table selection ───────────────────────────────────────────────────────
  /** Clicking the already-selected row closes the dialog, as on the users page. */
  selectTeam(team: ITeam): void {
    if (this.selectedTeam?.id === team.id) {
      this.closeDetailDialog();
      return;
    }

    this.openDetailDialog(team);
  }

  closeDetailDialog(): void {
    this.teamDetailDialogRef?.close();
    this.teamDetailDialogRef = null;
    this.selectedTeam = null;
  }

  onDetailDialogEdit(team: ITeam): void {
    this.closeDetailDialog();
    this.editTeam(team);
  }

  onDetailDialogDelete(team: ITeam): void {
    this.closeDetailDialog();
    this.deleteTeam(team);
  }

  /** True while the search box narrows the list, so the empty state can say which. */
  get hasSearchTerm(): boolean {
    return (this.searchControl.value ?? '').trim().length > 0;
  }

  // ── Row actions ───────────────────────────────────────────────────────────
  editTeam(team?: ITeam): void {
    this.teamFormDialogTeam = team ?? null;
    this.openTeamFormDialog();
  }

  deleteTeam(team: ITeam): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (!result) return;

        this.store.dispatch(SetTeamsLoader({ isLoading: true }));
        this.store.dispatch(DeleteTeam({ id: team.id }));
        if (this.selectedTeam?.id === team.id) this.closeDetailDialog();
      });
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────
  /**
   * The API orders the rows: `sort_by` is one of name / member_count / last_edited and
   * `sort_mode` is lowercase, or the request is a 400. Nothing is sorted in the browser.
   */
  onSort(field: TeamSortField): void {
    const nextSortMode: TeamSortMode =
      this.sortField === field && this.sortMode === 'asc' ? 'desc' : 'asc';

    this.sortField = field;
    this.sortMode = nextSortMode;

    this.store.dispatch(SetTeamsLoader({ isLoading: true }));
    this.store.dispatch(FetchAllTeams({ sort: { sort_by: field, sort_mode: nextSortMode } }));
  }

  // ── Template helpers ──────────────────────────────────────────────────────
  trackByTeamId(_: number, team: ITeam): number {
    return team.id;
  }

  trackByMemberId(_: number, member: ITeamMember): number {
    return member.id;
  }

  /** The square chip in the name cell: the team's first two letters. */
  getTeamInitials(team: ITeam): string {
    const name = (team?.name ?? '').trim();
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.charAt(1).toLowerCase();
  }

  getMemberCount(team: ITeam): number {
    if (typeof team?.member_count === 'number') return team.member_count;
    return Array.isArray(team?.members) ? team.members.length : 0;
  }

  /**
   * `last_edited` is a pre-formatted `"YYYY-MM-DD HH:mm:ss"` string, not an ISO
   * datetime — `new Date()` is not reliably parseable across browsers for that shape,
   * and there is no comparable formatted field elsewhere in the app to copy. So the
   * parts are read straight out of the string: no Date, no timezone, no locale.
   */
  formatLastEdited(value: string | null | undefined): string {
    if (!value) return '';

    const parts = LAST_EDITED_PATTERN.exec(value.trim());
    if (!parts) return value;

    const [, year, month, day, hours, minutes] = parts;
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  // ── Form dialog lifecycle ─────────────────────────────────────────────────
  private onTeamFormDialogSave(payload: TeamFormPayload): void {
    const selectedTeam = this.teamFormDialogTeam;

    if (selectedTeam?.id) {
      this.store.dispatch(UpdateTeam({
        id: selectedTeam.id,
        team: {
          name: payload.name,
          description: payload.description,
          // Forwarded as-is, empty included: `[]` is how the API clears a membership.
          member_ids: payload.member_ids,
        },
      }));
      return;
    }

    this.store.dispatch(AddTeam({
      team: {
        name: payload.name,
        description: payload.description,
        member_ids: payload.member_ids,
      },
    }));
  }

  private openTeamFormDialog(): void {
    const pendingTeam = this.teamFormDialogTeam;
    this.closeDetailDialog();
    this.closeTeamFormDialog();
    this.teamFormDialogTeam = pendingTeam;

    const ref = this.dialog.open(TeamFormDialogComponent, {
      data: {
        team: pendingTeam,
      },
      panelClass: 'settings-user-form-dialog',
      disableClose: true,
    });
    this.teamFormDialogRef = ref;

    ref.componentInstance.save
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((payload) => this.onTeamFormDialogSave(payload));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.teamFormDialogRef === ref) {
          this.teamFormDialogRef = null;
          this.teamFormDialogTeam = null;
        }
      });
  }

  // ── Detail dialog lifecycle ───────────────────────────────────────────────
  private openDetailDialog(team: ITeam): void {
    this.closeDetailDialog();

    const ref = this.dialog.open(TeamDetailDialogComponent, {
      panelClass: 'settings-team-detail-dialog',
      disableClose: false,
    });
    this.teamDetailDialogRef = ref;
    this.selectedTeam = team;
    ref.componentInstance.setTeam(team);

    ref.componentInstance.editTeam
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((targetTeam) => this.onDetailDialogEdit(targetTeam));

    ref.componentInstance.deleteTeam
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((targetTeam) => this.onDetailDialogDelete(targetTeam));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.teamDetailDialogRef === ref) {
          this.teamDetailDialogRef = null;
          this.selectedTeam = null;
        }
      });
  }

  private closeTeamFormDialog(): void {
    this.teamFormDialogRef?.close();
    this.teamFormDialogRef = null;
    this.teamFormDialogTeam = null;
  }

  /**
   * The mutation effects already re-dispatch `FetchAllTeams({})`, which the API answers
   * with its default `name asc`. Re-ask only when a column sort is active, so the list
   * does not silently jump back to the default under the user's chosen header.
   */
  private refetchWithCurrentSort(): void {
    if (!this.sortField) return;

    this.store.dispatch(
      FetchAllTeams({ sort: { sort_by: this.sortField, sort_mode: this.sortMode } }),
    );
  }
}
